import React, { useState } from "react";
import { m as motion } from "../../shared/components/Motion";
import "./skills.css";
import SkillContent from "./SkillContent";
import SkillsSkeleton from "./SkillsSkeleton";
import { useSkillsData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditSkillsModal from "../../features/admin/modals/EditSkillsModal";
import {
  containerVariants,
  childVariantsMedium as childVariants,
} from "../../shared/utils";

const Skills = () => {
  const { data, loading } = useSkillsData();
  const { adminMode, isAdmin, skillsDraft, saveSkillsDraft } = useAdmin();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [isNewGroup, setIsNewGroup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeIndex, setActiveIndex] = useState(0);

  // Use draft ONLY if admin is authenticated, otherwise use published data
  const displayData = React.useMemo(() => {
    return isAdmin && skillsDraft ? skillsDraft : data || {};
  }, [isAdmin, skillsDraft, data]);

  const groups = React.useMemo(() => {
    return Array.isArray(displayData?.groups) ? displayData.groups : [];
  }, [displayData]);

  const skillsNav = React.useMemo(() => {
    if (!groups.length) return [{ name: "all" }];
    return [
      { name: "all" },
      ...groups.map((group) => ({ name: group.groupTitle || "untitled" })),
    ];
  }, [groups]);

  const allSkills = React.useMemo(
    () =>
      groups.flatMap((group, index) =>
        (group.skills || []).map((skill, skillIndex) => ({
          ...skill,
          category: group.groupTitle || "untitled",
          groupIndex: index,
          uniqueId: `${group.groupTitle || "untitled"}-${skill.name}-${skillIndex}`,
        })),
      ),
    [groups],
  );

  const selectedGroupIndex = React.useMemo(() => {
    if (selectedCategory === "all") return -1;
    return groups.findIndex(
      (group) =>
        (group.groupTitle || "untitled").toLowerCase() ===
        selectedCategory.toLowerCase(),
    );
  }, [groups, selectedCategory]);

  const canAddGroup = adminMode && groups.length < 6;
  const canManageSelectedGroup = adminMode && selectedGroupIndex >= 0;

  const handleEditGroup = (index) => {
    setEditingGroupIndex(index);
    setIsNewGroup(false);
    setIsModalOpen(true);
  };

  const handleDeleteGroup = (index) => {
    const currentGroups = displayData?.groups || [];
    const newGroups = currentGroups.filter((_, idx) => idx !== index);
    saveSkillsDraft({ groups: newGroups }).catch((error) =>
      console.error("Error saving skills draft:", error),
    );
  };

  const handleAddNewGroup = () => {
    setEditingGroupIndex(null);
    setIsNewGroup(true);
    setIsModalOpen(true);
  };

  const handleCategoryClick = (index, categoryName) => {
    setSelectedCategory(categoryName.toLowerCase());
    setActiveIndex(index);
  };

  const handleSaveGroup = (updatedGroup) => {
    const currentGroups = displayData?.groups || [];
    let newGroups;

    if (updatedGroup === null) {
      // Delete group
      newGroups = currentGroups.filter((_, idx) => idx !== editingGroupIndex);
    } else if (isNewGroup) {
      // Add new group
      newGroups = [...currentGroups, updatedGroup];
    } else {
      // Update existing group
      newGroups = currentGroups.map((group, idx) =>
        idx === editingGroupIndex ? updatedGroup : group,
      );
    }

    saveSkillsDraft({ groups: newGroups }).catch((error) =>
      console.error("Error saving skills draft:", error),
    );
  };

  // Show skeleton while loading
  if (loading || !displayData?.groups) {
    return <SkillsSkeleton />;
  }

  return (
    <motion.section
      className="skills section"
      id="skills"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.h2 className="section__title" variants={childVariants}>
        Skills
      </motion.h2>
      <motion.span className="section__subtitle" variants={childVariants}>
        My technical level
      </motion.span>

      <motion.div className="skills__controlsRow" variants={childVariants}>
        <div className="skills__filtersNav">
          <div className="skills__filters">
            {skillsNav.map((item, index) => (
              <div className="skills__itemWrap" key={`${item.name}-${index}`}>
                <span
                  onClick={() => handleCategoryClick(index, item.name)}
                  className={`skills__item ${activeIndex === index ? "active-skills" : ""}`}
                >
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {canAddGroup && (
          <motion.button
            className="skills__navAddBtn"
            onClick={handleAddNewGroup}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            title="Add new skills group"
          >
            <i className="uil uil-plus"></i>
          </motion.button>
        )}
      </motion.div>

      <motion.div
        className="skills__container container"
        variants={containerVariants}
      >
        <SkillContent skills={allSkills} selectedCategory={selectedCategory} />
      </motion.div>

      {canManageSelectedGroup && (
        <motion.div className="skills__bottomActions" variants={childVariants}>
          <div className="skills__itemActions">
            {canManageSelectedGroup && (
              <>
                <motion.button
                  onClick={() => handleEditGroup(selectedGroupIndex)}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  title="Edit skills group"
                >
                  <i className="uil uil-edit-alt"></i>
                </motion.button>
                <motion.button
                  onClick={() => handleDeleteGroup(selectedGroupIndex)}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  title="Delete skills group"
                >
                  <i className="uil uil-trash-alt"></i>
                </motion.button>
              </>
            )}
          </div>
        </motion.div>
      )}

      {/* Edit Modal */}
      {isModalOpen && (
        <EditSkillsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          groupData={isNewGroup ? null : displayData?.groups[editingGroupIndex]}
          onSave={handleSaveGroup}
        />
      )}
    </motion.section>
  );
};

export default Skills;
